// frontend/src/components/client/Email/AutoresponderTab.tsx
/**
 * Autoresponder editor — one row per mailbox.
 *
 * The mailbox dropdown drives which row we're editing. Selecting a
 * mailbox loads its current autoresponder (if any); the form lets the
 * tenant edit subject + body, toggle on/off, save, or delete entirely.
 *
 * Vacation suppression window (7 days) is hardcoded server-side per
 * RFC 5230 — same external sender doesn't get bombarded on every reply
 * in a thread. Documented in the user manual.
 */
import React, { useEffect, useState } from 'react';
import {
  emailAutorespondersAPI,
  type Autoresponder,
} from '@hpanel/web-common/api/email_autoresponders';
import type { Mailbox } from '@hpanel/web-common/api/types';

interface Props {
  mailboxes: Mailbox[];
  autoresponders: Autoresponder[];
  mailboxAddress: (mb: Mailbox) => string;
  onChange: () => Promise<void>;
  onError: (msg: string | null) => void;
}

interface FormState {
  subject: string;
  body: string;
  is_enabled: boolean;
}

const EMPTY: FormState = {
  subject: 'Out of office',
  body:
    "Thank you for your message. I'm currently away and will reply when I return.",
  is_enabled: true,
};

export const AutoresponderTab: React.FC<Props> = ({
  mailboxes,
  autoresponders,
  mailboxAddress,
  onChange,
  onError,
}) => {
  const [selectedId, setSelectedId] = useState<number | null>(
    mailboxes[0]?.id ?? null,
  );
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const byMailbox = new Map(autoresponders.map((a) => [a.mailbox_id, a]));
  const currentAR = selectedId != null ? byMailbox.get(selectedId) : undefined;

  useEffect(() => {
    if (currentAR) {
      setForm({
        subject: currentAR.subject,
        body: currentAR.body,
        is_enabled: currentAR.is_enabled,
      });
    } else {
      setForm(EMPTY);
    }
    setFlash(null);
  }, [selectedId, currentAR?.id]);

  const handleSave = async () => {
    if (selectedId == null) return;
    setBusy(true);
    onError(null);
    try {
      await emailAutorespondersAPI.upsert(selectedId, form);
      await onChange();
      setFlash(
        form.is_enabled
          ? 'Autoresponder is on. Auto-replies will start with the next message.'
          : 'Autoresponder is saved but turned off — no auto-replies will be sent.',
      );
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (selectedId == null) return;
    if (!window.confirm('Remove this autoresponder entirely?')) return;
    setBusy(true);
    onError(null);
    try {
      await emailAutorespondersAPI.remove(selectedId);
      await onChange();
      setForm(EMPTY);
      setFlash('Autoresponder removed.');
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (mailboxes.length === 0) {
    return (
      <div
        data-testid="autoresponder-empty"
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-sm text-gray-600 dark:text-gray-400"
      >
        Create a mailbox first — autoresponders sit on individual addresses.
      </div>
    );
  }

  return (
    <div data-testid="autoresponder-tab" className="space-y-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Mailbox
          </label>
          <select
            data-testid="autoresponder-mailbox"
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            className="w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
          >
            {mailboxes.map((mb) => {
              const has = byMailbox.has(mb.id);
              return (
                <option key={mb.id} value={mb.id}>
                  {mailboxAddress(mb)} {has ? '• configured' : ''}
                </option>
              );
            })}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="ar-enabled"
            data-testid="autoresponder-enabled"
            type="checkbox"
            checked={form.is_enabled}
            onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
            className="h-4 w-4"
          />
          <label
            htmlFor="ar-enabled"
            className="text-sm text-gray-700 dark:text-gray-300"
          >
            Send auto-replies
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Subject
          </label>
          <input
            data-testid="autoresponder-subject"
            type="text"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            maxLength={255}
            className="w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Message
          </label>
          <textarea
            data-testid="autoresponder-body"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            rows={6}
            maxLength={4096}
            className="w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600 font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Up to 4096 characters. Each external sender gets at most one
            auto-reply every 7 days.
          </p>
        </div>

        {flash && (
          <div
            data-testid="autoresponder-flash"
            className="px-3 py-2 text-sm rounded bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200"
          >
            {flash}
          </div>
        )}

        <div className="flex justify-end gap-2">
          {currentAR && (
            <button
              data-testid="autoresponder-delete"
              onClick={handleDelete}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded-md border border-red-600 text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              Remove
            </button>
          )}
          <button
            data-testid="autoresponder-save"
            onClick={handleSave}
            disabled={busy || !form.subject.trim() || !form.body.trim()}
            className="px-4 py-1.5 text-sm rounded-md bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50"
          >
            {busy ? '…' : currentAR ? 'Save changes' : 'Create autoresponder'}
          </button>
        </div>
      </div>
    </div>
  );
};
